import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { integrationsService } from '../services/integrationsService';
import type {
  CalendarEventCreate,
  CalendarEventDetail,
  CalendarEventUpdate,
} from '../types/integrations';

export const calendarKeys = {
  all: ['calendar-events'] as const,
  range: (fromIso: string, toIso: string) =>
    [...calendarKeys.all, 'range', fromIso, toIso] as const,
  byId: (eventId: string) => [...calendarKeys.all, 'event', eventId] as const,
};

interface UseCalendarEventsArgs {
  from: Date;
  to: Date;
  enabled?: boolean;
}

export function useCalendarEvents({ from, to, enabled = true }: UseCalendarEventsArgs) {
  return useQuery({
    queryKey: calendarKeys.range(from.toISOString(), to.toISOString()),
    queryFn: () => integrationsService.listEvents(from, to),
    staleTime: 60_000,
    enabled,
    select: (envelope) => envelope.data?.events ?? [],
  });
}

export function useEventDetail(eventId: string | null, calendarId?: string) {
  return useQuery({
    queryKey: [...calendarKeys.byId(eventId ?? 'none'), calendarId ?? null] as const,
    queryFn: () => integrationsService.getEvent(eventId!, calendarId),
    enabled: !!eventId,
    staleTime: 5 * 60_000,
    select: (envelope): CalendarEventDetail => envelope.data,
  });
}

const errorCodeToCopy = (code: string | undefined, fallback: string): string => {
  switch (code) {
    case 'NOT_CONNECTED':
      return 'Connect Google Calendar in Settings → Integrations to manage events.';
    case 'REAUTH_REQUIRED':
      return 'Reconnect Google Calendar — your access has expired.';
    case 'OWNERSHIP_VIOLATION':
      return 'This event was created outside Tereiko. Manage it in Google Calendar.';
    case 'NOT_FOUND':
      return 'Event not found — it may have been deleted in Google Calendar.';
    case 'CALENDAR_ERROR':
      return "Couldn't reach Google Calendar. Try again in a moment.";
    default:
      return fallback;
  }
};

const surfaceError = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (data?.code === 'VALIDATION_ERROR') {
    return data;
  }
  toast.error(errorCodeToCopy(data?.code, data?.message || fallback));
  return data;
};

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CalendarEventCreate) => integrationsService.createEvent(payload),
    onSuccess: () => {
      toast.success('Event created');
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (err: any) => surfaceError(err, 'Could not create event. Please try again.'),
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, patch }: { eventId: string; patch: CalendarEventUpdate }) =>
      integrationsService.updateEvent(eventId, patch),
    onSuccess: () => {
      toast.success('Event updated');
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (err: any) => surfaceError(err, 'Could not update event. Please try again.'),
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, calendarId }: { eventId: string; calendarId?: string }) =>
      integrationsService.deleteEvent(eventId, calendarId),
    onSuccess: () => {
      toast.success('Event deleted');
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (err: any) => surfaceError(err, 'Could not delete event. Please try again.'),
  });
}
